'use client';

import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { userSchema } from '@/schema/client/schema';
import useFetch from '@/hook/R_query/useFetch';
import { useAppDispatch, useAppSelector } from '@/lib/RTK/hooks';
import { useToast } from '@/components/ui/use-toast';
import { useRouter } from 'next/navigation';
import { saveName, saveRegister, setToken } from '@/lib/RTK/features/AuthSlice';
import { LoadingButton } from '@/components/ui/loading-button';
import { axiosClientInstance } from '@/lib/servies/axiosConfig';
import { CustomFieldInput } from '@/components/index';
import { z } from 'zod';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Combobox } from '@/components/utils/combobox';
import { Controller } from 'react-hook-form';

type FormData = z.infer<typeof userSchema>;

const FormRegister = ({
    redirecTbHomePlus,
    onSuccess,
    className,
}: {
    redirecTbHomePlus?: boolean;
    className?: string;
    inputStyles?: string;
    onSuccess?: () => void;
}) => {
    const {
        register,
        handleSubmit,
        setValue,
        control,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(userSchema),
    });

    const [loading, setLoading] = useState(false);

    const { data: provincesData, isLoading: isLoadingProvinces } = useFetch(
        'get_province',
        'get_province',
    );
    const [provinceId, setProvinceId] = useState<string | null>(null);
    const {
        data: citiesData,
        isLoading: isLoadingCities,
        refetch: fetchCities,
    } = useFetch('get_city', `get_city/${provinceId}`);
    const { toast } = useToast();
    const router = useRouter();
    const dispatch = useAppDispatch();
    const { mobile, token } = useAppSelector((state) => ({
        mobile: state.auth_token_slice.mobile,
        token: state.auth_token_slice.token,
    }));
    const [state, setState] = useState({
        state: '',
        stateId: null,
        city: '',
        cityId: null,
    });
    const onSubmit: SubmitHandler<FormData> = async (formData) => {
        const { firstName, lastName, national_code } = formData;
        const { cityId } = state;
        setLoading(true);
        try {
            const response = await axiosClientInstance.post(
                '/user/register_complete',
                {
                    mobile: mobile,
                    national_code: national_code,
                    name: `${firstName} ${lastName}`,
                    main_family: lastName,
                    city_id: cityId,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            if (response.status === 200) {
                if (response.data.status === 0) {
                    toast({
                        title: response.data.message,
                        variant: 'error',
                        className:
                            ' top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4',
                    });
                    return;
                }

                toast({
                    title: response.data.message,
                    variant: 'success',
                    className:
                        ' top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4',
                });

                dispatch(setToken(response.data.token));
                dispatch(saveName(response.data.result?.name));
                dispatch(saveRegister('0'));
                if (redirecTbHomePlus) router.replace('/');
                else onSuccess?.();
            }
        } catch {
            toast({
                title: 'خطایی پیش آمده است !',
                variant: 'error',
                className:
                    'top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleStateChange = (value: string) => {
        const selectedProvince = provincesData?.result.province.find(
            (province: { id: string }) => province.id == value,
        );
        const selectedProvinceId = selectedProvince
            ? selectedProvince.id
            : null;

        setState((prevState) => ({
            ...prevState,
            state: value,
            stateId: selectedProvinceId,
            city: '',
            cityId: null,
        }));
        setValue('state', value);
        setProvinceId(selectedProvinceId);
    };

    const handleCityChange = (value: string) => {
        const selectedCity = citiesData?.result.city.find(
            (city: { id: string }) => city.id == value,
        );
        const selectedCityId = selectedCity ? selectedCity.id : null;

        setState((prevState) => ({
            ...prevState,
            city: value,
            cityId: selectedCityId,
        }));
        setValue('city', value);
    };

    useEffect(() => {
        if (provinceId) {
            fetchCities();
        }
    }, [provinceId, fetchCities]);

    return (
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
            <div
                className={`${
                    className ? className : ''
                } flex flex-col gap-5 w-full mt-2`}
            >
                <CustomFieldInput
                    label="نام *"
                    type="text"
                    styleInput={
                        'border-gray-200 !rounded-2xl !bg-white px-3 py-2 !text-sm outline-none !ring-0'
                    }
                    labelStyle="pb-2 !bg-transparent !block !text-[#222222] !text-sm font-semibold"
                    placeholder="نام"
                    name="firstName"
                    register={register}
                    error={errors.firstName}
                />
                <CustomFieldInput
                    label="نام خانوادگی *"
                    type="text"
                    styleInput={
                        'border-gray-200 !rounded-2xl !bg-white px-3 py-2 !text-sm outline-none !ring-0'
                    }
                    labelStyle="pb-2 !bg-transparent !block !text-[#222222] !text-sm font-semibold"
                    placeholder="خانوادگی"
                    name="lastName"
                    register={register}
                    error={errors.lastName}
                />
                <CustomFieldInput
                    label="کد ملی *"
                    type="text"
                    styleInput={
                        'border-gray-200 !rounded-2xl !bg-white px-3 py-2 !text-sm outline-none !ring-0'
                    }
                    labelStyle="pb-2 !bg-transparent !block !text-[#222222] !text-sm font-semibold"
                    placeholder="کد ملی"
                    name="national_code"
                    register={register}
                    error={errors.national_code}
                />
                {!isLoadingProvinces && (
                    <div className="md:!pt-0 pt-2">
                        <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                                <Combobox
                                    placeholder="استان"
                                    trashClassName="!h-[2.3rem] !mt-0"
                                    options={provincesData?.result?.province.map(
                                        (pr: { name: string; id: string }) => ({
                                            label: pr.name,
                                            value: String(pr.id),
                                        }),
                                    )}
                                    value={field.value}
                                    btnClassName="!bg-white !rounded-2xl !h-[37.6px]"
                                    reverseColoring
                                    onChange={(
                                        selectedItem: string | undefined,
                                    ) => {
                                        field.onChange(selectedItem);
                                        handleStateChange(String(selectedItem));
                                    }}
                                    removeHandler={() => {
                                        field.onChange('');
                                        handleStateChange('');
                                        handleCityChange('');
                                    }}
                                />
                            )}
                        />
                        {errors.state && (
                            <p className="text-red-400 text-xs pt-1">
                                {errors.state.message}
                            </p>
                        )}
                    </div>
                )}
                <div className="pt-2">
                    <Controller
                        name="city"
                        control={control}
                        render={({ field }) => (
                            <Combobox
                                placeholder="شهر"
                                trashClassName="!h-[2.3rem] !mt-0"
                                options={
                                    citiesData?.result?.city.map(
                                        (pr: { name: string; id: string }) => ({
                                            label: pr.name,
                                            value: String(pr.id),
                                        }),
                                    ) || []
                                }
                                value={field.value}
                                btnClassName="!bg-white !rounded-2xl !h-[37.6px]"
                                reverseColoring
                                onChange={(
                                    selectedItem: string | undefined,
                                ) => {
                                    field.onChange(selectedItem);
                                    handleCityChange(String(selectedItem));
                                }}
                                removeHandler={() => {
                                    field.onChange('');
                                    handleCityChange('');
                                }}
                                disabled={!state.state || isLoadingCities}
                            />
                        )}
                    />
                    {errors.city && (
                        <p className="text-red-400 text-xs pt-1">
                            {errors.city.message}
                        </p>
                    )}
                </div>
            </div>

            <LoadingButton
                className="w-full bg-primary !mt-5 !bg-blue !rounded-2xl text-white md:text-[16px] text-[13px] "
                disabled={loading}
                type="submit"
            >
                {loading ? (
                    <AiOutlineLoading3Quarters className="animate-spin" />
                ) : (
                    'ثبت'
                )}
            </LoadingButton>
        </form>
    );
};

export default FormRegister;
